import { useState } from 'react';
import {
  InlineNotification,
  Tag,
  Toggle,
  Modal,
  Loading,
} from '@carbon/react';
import type { CarbonIconType } from '@carbon/icons-react';
import { WarningAlt, Document, Locked } from '@carbon/icons-react';
import {
  useConsents,
  useGrantConsent,
  useWithdrawConsent,
} from '@sphere/user/data-access';
import {
  CONSENT_LABELS,
  ConsentKind,
  type UserConsent,
} from '@sphere/user/domain';
import { toast } from 'sonner';
import { ErrorView } from '@sphere/shared/ui/design-system';
import { formatDateFrShort } from '@sphere/shared/util';
import { SettingsSection, SettingsSectionItem } from './components';
import shared from './settings-pages.shared.module.scss';
import styles from './PrivacyPage.module.scss';

const CONSENT_KINDS: ConsentKind[] = [
  ConsentKind.TERMS,
  ConsentKind.PRIVACY,
  ConsentKind.MARKETING,
  ConsentKind.ANALYTICS,
  ConsentKind.COOKIES,
];

const CONSENT_ICONS: Partial<Record<ConsentKind, CarbonIconType>> = {
  [ConsentKind.TERMS]: Document,
  [ConsentKind.TERMS_OF_SERVICE]: Document,
  [ConsentKind.PRIVACY]: Locked,
  [ConsentKind.PRIVACY_POLICY]: Locked,
  [ConsentKind.MARKETING]: Document,
  [ConsentKind.ANALYTICS]: Document,
  [ConsentKind.COOKIES]: Document,
  [ConsentKind.DATA_SHARING]: Document,
  [ConsentKind.DATA_PROCESSING]: Document,
  [ConsentKind.AUP]: Locked,
  [ConsentKind.CODE_OF_CONDUCT]: Locked,
  [ConsentKind.PARENTAL_CONSENT]: Locked,
  [ConsentKind.MEDIA_RELEASE]: Document,
};

const CURRENT_VERSION = 'v1.0';

export default function PrivacyPage() {
  const { data: consents, isLoading, error, refetch } = useConsents();
  const grantConsent = useGrantConsent();
  const withdrawConsent = useWithdrawConsent();
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    kind: ConsentKind | null;
    action: 'grant' | 'withdraw';
  }>({ open: false, kind: null, action: 'grant' });

  const getConsentStatus = (kind: ConsentKind): UserConsent | undefined => {
    if (!Array.isArray(consents)) return undefined;
    return consents.find(
      (c) => c.kind === kind && c.version === CURRENT_VERSION && !c.withdrawnAt
    );
  };

  const isGranted = (kind: ConsentKind) => {
    const consent = getConsentStatus(kind);
    return consent?.isGranted ?? false;
  };

  const handleToggle = (kind: ConsentKind, checked: boolean) => {
    if (
      (kind === ConsentKind.TERMS || kind === ConsentKind.PRIVACY) &&
      !checked
    ) {
      toast.error('Ce consentement est obligatoire');
      return;
    }

    setConfirmDialog({
      open: true,
      kind,
      action: checked ? 'grant' : 'withdraw',
    });
  };

  const confirmAction = async () => {
    if (!confirmDialog.kind) return;

    try {
      if (confirmDialog.action === 'grant') {
        await grantConsent.mutateAsync({
          kind: confirmDialog.kind,
          version: CURRENT_VERSION,
          isGranted: true,
        });
        toast.success('Consentement accordé');
      } else {
        await withdrawConsent.mutateAsync({
          kind: confirmDialog.kind,
          version: CURRENT_VERSION,
        });
        toast.success('Consentement retiré');
      }
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setConfirmDialog({ open: false, kind: null, action: 'grant' });
    }
  };

  const isPending = grantConsent.isPending || withdrawConsent.isPending;

  if (isLoading) {
    return <PrivacySkeleton />;
  }

  if (error) {
    return (
      <div className={shared.page}>
        <div className={shared.statePanel}>
          <ErrorView
            title="Erreur de chargement"
            message="Impossible de charger vos consentements."
            error={error as Error}
            onRetry={() => refetch()}
            variant="card"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={shared.page}>
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="Information RGPD"
        subtitle="Conformément au RGPD, vous pouvez à tout moment consulter, modifier ou retirer vos consentements. Certains consentements sont obligatoires pour utiliser le service."
      />

      <SettingsSection
        title="Consentements"
        description="Gérez vos accords pour l'utilisation de vos données"
        icon={Document}
      >
        {CONSENT_KINDS.map((kind) => {
          const Icon = CONSENT_ICONS[kind] ?? Document;
          const granted = isGranted(kind);
          const consent = getConsentStatus(kind);
          const isRequired =
            kind === ConsentKind.TERMS || kind === ConsentKind.PRIVACY;

          return (
            <SettingsSectionItem
              key={kind}
              icon={Icon}
              labelId={`consent-${kind}-label`}
              label={CONSENT_LABELS[kind]?.title ?? kind}
              labelSuffix={
                isRequired ? <Tag type="gray">Obligatoire</Tag> : undefined
              }
              sublabel={
                <>
                  {CONSENT_LABELS[kind]?.description}
                  {consent?.grantedAt && (
                    <span className={styles.consentDate}>
                      {' '}
                      Accordé le {formatDateFrShort(consent.grantedAt)}
                    </span>
                  )}
                </>
              }
              action={
                <div className={styles.consentToggle}>
                  <Toggle
                    id={`consent-${kind}-toggle`}
                    labelText=""
                    labelA="Non"
                    labelB="Oui"
                    aria-labelledby={`consent-${kind}-label`}
                    toggled={granted}
                    onToggle={(checked) => handleToggle(kind, checked)}
                    disabled={isPending || (isRequired && granted)}
                  />
                </div>
              }
            />
          );
        })}
      </SettingsSection>

      <Modal
        open={confirmDialog.open}
        onRequestClose={() =>
          setConfirmDialog({ open: false, kind: null, action: 'grant' })
        }
        modalHeading={
          <span className={styles.modalHeading}>
            <WarningAlt size={20} className={styles.modalIcon} />
            Confirmer l'action
          </span>
        }
        primaryButtonText="Confirmer"
        secondaryButtonText="Annuler"
        onSecondarySubmit={() =>
          setConfirmDialog({ open: false, kind: null, action: 'grant' })
        }
        onRequestSubmit={confirmAction}
        primaryButtonDisabled={isPending}
      >
        <p>
          {confirmDialog.action === 'grant'
            ? `Voulez-vous accorder votre consentement pour "${confirmDialog.kind ? CONSENT_LABELS[confirmDialog.kind]?.title : ''}" ?`
            : `Voulez-vous retirer votre consentement pour "${confirmDialog.kind ? CONSENT_LABELS[confirmDialog.kind]?.title : ''}" ?`}
        </p>
      </Modal>

      {isPending && (
        <div className={shared.savingIndicator}>
          <Loading small withOverlay={false} />
          Enregistrement...
        </div>
      )}
    </div>
  );
}

function PrivacySkeleton() {
  return (
    <div className={shared.page}>
      <div
        className="cds--skeleton cds--skeleton--text"
        style={{ height: '4rem', width: '100%' }}
      />
      <SettingsSection title=" " description=" ">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="cds--skeleton cds--skeleton--text"
            style={{ height: '6rem' }}
          />
        ))}
      </SettingsSection>
    </div>
  );
}
